// Projecttaken (/mijn-taken): al je openstaande taken, rustig per project.
//
// Planning-gauntlet stap 2 (25 sep 2026): geen chips meer (project, type en een gekleurde
// prio per rij), maar één kolomkop en grijze tekst; alleen een verlopen deadline is rood.
// Een klik op een rij opent het gedeelde TaakPaneel; per project voeg je met een stille
// regel direct een taak toe (Enter), met jou als uitvoerder.
// Stap 3: tab "Kalender" (?weergave=kalender, &bereik=2w) met dezelfde taken in een raster;
// slepen verzet start én eind (TakenKalender).
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box, Typography, CircularProgress, ToggleButton, ToggleButtonGroup, MenuItem, TextField,
} from '@mui/material';
import PageHeader from '../../ui-components/PageHeader';
import TaakPaneel from '../../ui-components/TaakPaneel/TaakPaneel';
import TakenKalender from './TakenKalender';
import { sleepVelden } from './kalenderLogica';
import { useMsal } from '@azure/msal-react';
import { authFetch } from '../../utils/authHelpers';
import { leesJson, meldFout } from '../../utils/apiFout';
import { BACKEND_URL } from '../../utils/apiConfig';
import { formatDateISO } from '../../utils/weekUtils';
import { schrijfTaak } from '../../utils/planningBronnen';
import { biedOngedaanAan, registreerOngedaanSneltoets } from '../../utils/ongedaan';
import {
  vanRecord, isOpenstaand, sorteer, groepeer, werkRijBij, nieuweTaakBody, urenLabel, ZONDER_PROJECT,
} from './mijnTakenLijst';

const FONT = '"Inter", system-ui, sans-serif';
const TEKST = '#1f2937';
const GEDEMPT = '#6b7280';
const ZACHT = '#9ca3af';
const LIJN = '#eceef1';
const HOVER = '#f8fafc';
const ACCENT = '#526075';
const ROOD = '#b91c1c';

const KOLOMMEN = { xs: 'minmax(0,1fr) auto', md: 'minmax(0,1fr) 180px 160px 110px 64px' };

const datumKort = (iso) => {
  if (!iso) return '';
  const d = new Date(`${String(iso).slice(0, 10)}T12:00:00`);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString('nl-NL', { day: 'numeric', month: 'short' });
};

function KolomKop() {
  const cel = { fontFamily: FONT, fontSize: 11.5, color: ZACHT, fontWeight: 500 };
  return (
    <Box sx={{ display: { xs: 'none', md: 'grid' }, gridTemplateColumns: KOLOMMEN, gap: 2, px: 1.5, pb: 0.75, borderBottom: `1px solid ${LIJN}` }}>
      <Typography sx={cel}>Taak</Typography>
      <Typography sx={cel}>Fase / takenlijst</Typography>
      <Typography sx={cel}>Status</Typography>
      <Typography sx={cel}>Deadline</Typography>
      <Typography sx={{ ...cel, textAlign: 'right' }}>Uren</Typography>
    </Box>
  );
}

function TaakRij({ taak, toonProject, vandaag, gekozen, onOpen }) {
  const teLaat = !taak.afgerond && taak.deadline && taak.deadline < vandaag;
  const tweede = { fontFamily: FONT, fontSize: 12.5, color: GEDEMPT, display: { xs: 'none', md: 'block' } };
  return (
    <Box
      role="button" tabIndex={0} aria-label={`Open ${taak.naam}`}
      onClick={() => onOpen(taak)}
      onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); onOpen(taak); } }}
      sx={{
        display: 'grid', gridTemplateColumns: KOLOMMEN, gap: 2, alignItems: 'baseline',
        px: 1.5, py: 1, borderBottom: `1px solid ${LIJN}`, cursor: 'pointer',
        bgcolor: gekozen ? HOVER : 'transparent', '&:hover': { bgcolor: HOVER },
        '&:focus-visible': { outline: `2px solid ${ACCENT}`, outlineOffset: -2 },
      }}
    >
      <Box sx={{ minWidth: 0 }}>
        <Typography noWrap sx={{
          fontFamily: FONT, fontSize: 13.5, color: taak.afgerond ? ZACHT : TEKST,
          textDecoration: taak.afgerond ? 'line-through' : 'none',
        }}>
          {taak.naam}
        </Typography>
        {toonProject && taak.projectNaam && (
          <Typography noWrap sx={{ fontFamily: FONT, fontSize: 12, color: ZACHT }}>{taak.projectNaam}</Typography>
        )}
      </Box>
      <Typography noWrap sx={tweede}>{taak.fase || taak.type || ''}</Typography>
      <Typography noWrap sx={tweede}>{taak.afgerond ? 'Afgerond' : taak.status}</Typography>
      <Typography sx={{ ...tweede, color: teLaat ? ROOD : GEDEMPT }} data-te-laat={teLaat ? 'ja' : undefined}>
        {datumKort(taak.deadline)}
      </Typography>
      <Typography sx={{ fontFamily: FONT, fontSize: 12.5, color: GEDEMPT, textAlign: 'right' }}>
        {urenLabel(taak.uren)}
      </Typography>
    </Box>
  );
}

/** Stille regel onderaan een projectgroep: klik → invoerveld, Enter = aanmaken, Esc = stoppen. */
function SnelToevoegen({ projectKop, onVoegToe }) {
  const [actief, setActief] = useState(false);
  const [naam, setNaam] = useState('');
  const [bezig, setBezig] = useState(false);
  if (!actief) {
    return (
      <Box component="button" type="button" onClick={() => setActief(true)}
        sx={{ border: 0, bgcolor: 'transparent', cursor: 'pointer', px: 1.5, py: 0.9, textAlign: 'left', width: '100%',
          fontFamily: FONT, fontSize: 13, color: ZACHT, '&:hover': { color: GEDEMPT } }}>
        + taak toevoegen
      </Box>
    );
  }
  const bewaar = async () => {
    const schoon = naam.trim();
    if (!schoon || bezig) return;
    setBezig(true);
    const gelukt = await onVoegToe(schoon);
    setBezig(false);
    if (gelukt) setNaam('');
  };
  return (
    <Box sx={{ px: 1.5, py: 0.5 }}>
      <TextField
        autoFocus fullWidth variant="standard" value={naam} disabled={bezig}
        placeholder="Taaknaam… (Enter)"
        inputProps={{ 'aria-label': `Nieuwe taak voor ${projectKop}` }}
        InputProps={{ disableUnderline: true }}
        onChange={(e) => setNaam(e.target.value)}
        onBlur={() => { if (!naam.trim()) setActief(false); }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') { e.preventDefault(); bewaar(); }
          if (e.key === 'Escape') { e.preventDefault(); setNaam(''); setActief(false); }
        }}
        sx={{ '& .MuiInputBase-input': { fontFamily: FONT, fontSize: 13.5, py: 0.5 } }}
      />
    </Box>
  );
}

export default function MijnTaken() {
  const navigate = useNavigate();
  const { accounts } = useMsal();
  const userEmail = (accounts?.[0]?.username || '').toLowerCase();
  const [taken, setTaken] = useState(null);
  const [fout, setFout] = useState(null);
  const [groeperen, setGroeperen] = useState(true);
  const [sortering, setSortering] = useState('start');
  const [gekozenId, setGekozenId] = useState(null);
  const vandaag = formatDateISO(new Date());
  // Weergave en bereik in de URL: zo is de kalender te delen en komt hij terug na verversen.
  const [zoek, setZoek] = useSearchParams();
  const weergave = zoek.get('weergave') === 'kalender' ? 'kalender' : 'lijst';
  const bereik = zoek.get('bereik') === '2w' ? '2w' : 'maand';
  const anker = /^\d{4}-\d{2}-\d{2}$/.test(zoek.get('datum') || '') ? zoek.get('datum') : vandaag;
  const zetZoek = useCallback((wijz) => {
    setZoek((oud) => {
      const n = new URLSearchParams(oud);
      Object.entries(wijz).forEach(([k, v]) => { if (v) n.set(k, v); else n.delete(k); });
      return n;
    }, { replace: true });
  }, [setZoek]);

  useEffect(() => registreerOngedaanSneltoets(), []);

  const laad = useCallback(async () => {
    try {
      const r = await authFetch(`${BACKEND_URL}/Api/my-tasks?email=${encodeURIComponent(userEmail)}`);
      const d = await leesJson(r, 'Taken laden');
      setTaken((d.tasks || []).map(vanRecord).filter(isOpenstaand));
    } catch (e) {
      setFout(e.message || 'Taken laden mislukt.');
    }
  }, [userEmail]);
  useEffect(() => { laad(); }, [laad]);

  const groepen = useMemo(() => groepeer(taken || [], sortering), [taken, sortering]);
  const plat = useMemo(() => sorteer(taken || [], sortering), [taken, sortering]);
  const totaalUren = useMemo(() => (taken || []).reduce((s, t) => s + (t.uren || 0), 0), [taken]);
  const gekozen = useMemo(() => (taken || []).find((t) => t.id === gekozenId) || null, [taken, gekozenId]);

  const naWijziging = useCallback((id, velden) => {
    setTaken((prev) => (prev || []).map((t) => (t.id === id ? werkRijBij(t, velden) : t)));
  }, []);

  const voegToe = useCallback(async (groep, naam) => {
    const body = nieuweTaakBody({ naam, projectId: groep.projectId, email: userEmail });
    try {
      const r = await schrijfTaak(`${BACKEND_URL}/Api/tasks`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
      });
      const d = await leesJson(r, 'Taak toevoegen');
      const id = d?.task?.id || d?.id;
      if (!id) throw new Error('Taak toevoegen: geen id terug');
      // De engine geeft alleen de meegestuurde velden terug; de projectnaam weten we zelf.
      const { assigneeEmail, ...velden } = body;
      void assigneeEmail;
      const fields = { ...velden, ...(groep.kop !== ZONDER_PROJECT ? { 'Prnr en Naam': [groep.kop] } : {}) };
      setTaken((prev) => [...(prev || []), vanRecord({ id, fields, _onvolledig: true })]);
      // Vangnet: per ongeluk aangemaakt → met één klik (of Ctrl+Z) weer weg.
      biedOngedaanAan(`"${naam}" toegevoegd`, async () => {
        const rr = await schrijfTaak(`${BACKEND_URL}/Api/tasks/${encodeURIComponent(id)}`, { method: 'DELETE' });
        await leesJson(rr, 'Taak weghalen');
        setTaken((prev) => (prev || []).filter((t) => t.id !== id));
      });
      return true;
    } catch (e) {
      meldFout(e, 'Taak toevoegen');
      return false;
    }
  }, [userEmail]);

  const open = (t) => setGekozenId(t.id);

  // Kalender: een taak naar een andere dag slepen. Meteen in beeld, dan opslaan; mislukt het,
  // dan terug. Ongedaan maken (knop of Ctrl+Z) zet de oude start en eind terug.
  const verzet = useCallback(async (id, gepaktOp, doel) => {
    const taak = (taken || []).find((t) => t.id === id);
    const velden = taak ? sleepVelden(taak, gepaktOp, doel) : null;
    if (!velden) return;
    const terug = { StartDateUitvoerder: taak.start || null, EndDateUitvoerder: taak.eind || null };
    const schrijf = async (v) => {
      const r = await schrijfTaak(`${BACKEND_URL}/Api/tasks/${encodeURIComponent(id)}`, {
        method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(v),
      });
      await leesJson(r, 'Taak verzetten');
    };
    naWijziging(id, velden);
    try {
      await schrijf(velden);
    } catch (e) {
      naWijziging(id, terug);
      meldFout(e, 'Taak verzetten');
      return;
    }
    const wanneer = new Date(`${velden.StartDateUitvoerder}T12:00:00`)
      .toLocaleDateString('nl-NL', { weekday: 'short', day: 'numeric', month: 'short' });
    biedOngedaanAan(`"${taak.naam}" verzet naar ${wanneer}`, async () => {
      await schrijf(terug);
      naWijziging(id, terug);
    });
  }, [taken, naWijziging]);

  const weergaveKnop = (waarde, label) => (
    <Box component="button" type="button" role="tab" aria-selected={weergave === waarde}
      onClick={() => zetZoek({ weergave: waarde === 'kalender' ? 'kalender' : null })}
      sx={{
        border: 0, bgcolor: 'transparent', cursor: 'pointer', px: 0, py: 0.5, fontFamily: FONT, fontSize: 14,
        color: weergave === waarde ? TEKST : ZACHT, fontWeight: weergave === waarde ? 600 : 400,
        borderBottom: `2px solid ${weergave === waarde ? ACCENT : 'transparent'}`, '&:hover': { color: TEKST },
      }}>
      {label}
    </Box>
  );

  return (
    <Box sx={{ width: '100%', maxWidth: 1800, mx: 'auto', px: { xs: 2, sm: 3, md: 5 }, pt: { xs: 3, md: 4 }, pb: 4 }}>
      <PageHeader
        icon="checklist"
        title="Projecttaken"
        subtitle={taken ? `${taken.length} openstaande taken · ${Math.round(totaalUren)} uur` : 'Al je openstaande taken op één plek'}
        rightActions={weergave === 'kalender' ? null : (
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <ToggleButtonGroup size="small" exclusive value={groeperen ? 'project' : 'plat'}
              onChange={(_e, v) => v && setGroeperen(v === 'project')}>
              <ToggleButton value="project" sx={{ textTransform: 'none', fontWeight: 600, py: 0.25 }}>Per project</ToggleButton>
              <ToggleButton value="plat" sx={{ textTransform: 'none', fontWeight: 600, py: 0.25 }}>Alles</ToggleButton>
            </ToggleButtonGroup>
            <TextField select size="small" value={sortering} onChange={(e) => setSortering(e.target.value)}
              sx={{ minWidth: 150 }} label="Sorteer op">
              <MenuItem value="start">Startdatum</MenuItem>
              <MenuItem value="deadline">Deadline</MenuItem>
              <MenuItem value="prio">Prioriteit</MenuItem>
            </TextField>
          </Box>
        )}
      />

      <Box role="tablist" aria-label="Weergave" sx={{ display: 'flex', gap: 2.5, alignItems: 'baseline', mt: 1 }}>
        {weergaveKnop('lijst', 'Lijst')}
        {weergaveKnop('kalender', 'Kalender')}
      </Box>

      {fout && <Typography sx={{ fontFamily: FONT, color: ROOD, mt: 2 }}>{fout}</Typography>}
      {!taken && !fout && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress size={24} /></Box>
      )}

      {taken && taken.length === 0 && weergave === 'lijst' && (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Typography sx={{ fontFamily: FONT, color: GEDEMPT }}>Geen openstaande taken.</Typography>
        </Box>
      )}

      {taken && weergave === 'kalender' && (
        <TakenKalender
          taken={taken} vandaag={vandaag} gekozenId={gekozenId} onOpen={open} onVerzet={verzet}
          bereik={bereik} onBereik={(b) => zetZoek({ bereik: b === '2w' ? '2w' : null })}
          anker={anker} onAnker={(d) => zetZoek({ datum: d === vandaag ? null : d })}
        />
      )}

      {taken && taken.length > 0 && weergave === 'lijst' && (
        <Box sx={{ mt: 2 }}>
          <KolomKop />
          {groeperen ? groepen.map((g) => (
            <Box key={g.kop} component="section" aria-label={g.kop} sx={{ mt: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, px: 1.5, pb: 0.5, borderBottom: `1px solid ${LIJN}` }}>
                {g.projectId ? (
                  <Box component="button" type="button" onClick={() => navigate(`/projects/${g.projectId}`)}
                    sx={{ border: 0, bgcolor: 'transparent', p: 0, cursor: 'pointer', textAlign: 'left', minWidth: 0,
                      fontFamily: FONT, fontSize: 14, fontWeight: 600, color: TEKST,
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                      '&:hover': { textDecoration: 'underline' } }}>
                    {g.kop}
                  </Box>
                ) : (
                  <Typography noWrap sx={{ fontFamily: FONT, fontSize: 14, fontWeight: 600, color: TEKST }}>{g.kop}</Typography>
                )}
                <Typography sx={{ fontFamily: FONT, fontSize: 12.5, color: ZACHT, flexShrink: 0 }}>
                  {`· ${g.taken.length}${g.uren ? ` · ${urenLabel(g.uren)}` : ''}`}
                </Typography>
              </Box>
              {g.taken.map((t) => (
                <TaakRij key={t.id} taak={t} vandaag={vandaag} gekozen={t.id === gekozenId} onOpen={open} />
              ))}
              <SnelToevoegen projectKop={g.kop} onVoegToe={(naam) => voegToe(g, naam)} />
            </Box>
          )) : plat.map((t) => (
            <TaakRij key={t.id} taak={t} toonProject vandaag={vandaag} gekozen={t.id === gekozenId} onOpen={open} />
          ))}
        </Box>
      )}

      <TaakPaneel
        taak={gekozen?.raw || null}
        open={Boolean(gekozen)}
        onClose={() => setGekozenId(null)}
        onBijgewerkt={naWijziging}
        onVerwijderd={(id) => { setGekozenId(null); setTaken((prev) => (prev || []).filter((t) => t.id !== id)); }}
        onNaarProject={(pid) => { setGekozenId(null); navigate(`/projects/${pid}`); }}
        onAangemaakt={(rec) => {
          const rij = vanRecord({ ...rec, _onvolledig: true });
          if (isOpenstaand(rij)) setTaken((prev) => [...(prev || []).filter((t) => t.id !== rij.id), rij]);
        }}
        onWeggehaald={(id) => setTaken((prev) => (prev || []).filter((t) => t.id !== id))}
      />
    </Box>
  );
}
